import { useEffect, useState, useCallback } from "react";
import {
  HiOutlinePlay,
  HiOutlineRefresh,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineStop,
  HiOutlineChevronDown,
} from "react-icons/hi";
import {
  fetchCompetencies,
  fetchSetsForCompetency,
  fetchQuestionsForSet,
} from "../services/masterDataApi";
import Toast from "../components/Toast";

/* ── style tokens ──────────────────────────────────────────── */
const selectClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const labelClass = "block text-sm font-medium text-[#24292f] mb-1";
const btnPrimary =
  "flex items-center gap-1.5 px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] transition-all";

const STOP_RULE_LABELS = {
  "3_consecutive_wrong": "3 Consecutive Wrong → Stop",
  "4_consecutive_wrong": "4 Consecutive Wrong → Stop",
};

/* ═══════════════════════════════════════════════════════════════
   ASSESSMENT SIMULATOR
   Admin can pick a set and simulate answering MCQ questions
   to verify stop_rule logic works.
   ═══════════════════════════════════════════════════════════════ */
export default function AssessmentSimulator() {
  const [toast, setToast] = useState(null);

  /* ── Step 1: Select competency → set ── */
  const [competencies, setCompetencies] = useState([]);
  const [selectedComp, setSelectedComp] = useState("");
  const [sets, setSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("");

  /* ── Step 2: Questions + simulation state ── */
  const [questions, setQuestions] = useState([]);
  const [gridItems, setGridItems] = useState([]);
  const [setType, setSetType] = useState("mcq"); // "mcq" or "grid"
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // {questionId, selectedOption, isCorrect}
  const [gridAnswers, setGridAnswers] = useState([]); // {index, text, correct: bool}
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [finished, setFinished] = useState(false);
  const [started, setStarted] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const selectedSet = sets.find((s) => s.id === selectedSetId);
  const stopThreshold = selectedSet?.stop_rule === "3_consecutive_wrong"
    ? 3
    : selectedSet?.stop_rule === "4_consecutive_wrong"
    ? 4
    : null;

  /* ── Load competencies ── */
  useEffect(() => {
    fetchCompetencies()
      .then(setCompetencies)
      .catch(() => setToast({ type: "error", message: "Failed to load competencies" }));
  }, []);

  /* ── Load sets when competency changes ── */
  useEffect(() => {
    if (!selectedComp) { setSets([]); return; }
    fetchSetsForCompetency(selectedComp)
      .then(setSets)
      .catch(() => setToast({ type: "error", message: "Failed to load sets" }));
  }, [selectedComp]);

  /* ── Start simulation ── */
  const startSimulation = useCallback(async () => {
    if (!selectedSetId) return;
    try {
      const currentSet = sets.find((s) => s.id === selectedSetId);
      const type = currentSet?.set_type || "mcq";
      setSetType(type);

      if (type === "grid") {
        // Grid sets: items come from set's grid_items
        const items = currentSet?.grid_items || [];
        if (items.length === 0) {
          setToast({ type: "error", message: "No grid items in this set to simulate" });
          return;
        }
        setGridItems(items);
        setQuestions([]);
        setCurrentIdx(0);
        setGridAnswers([]);
        setAnswers([]);
        setConsecutiveWrong(0);
        setStopped(false);
        setFinished(false);
        setStarted(true);
        setSelectedOption(null);
      } else {
        // MCQ sets — only these need the questions call (grid sets carry
        // their items in the sets response).
        const qs = await fetchQuestionsForSet(selectedSetId);
        const mcqs = qs.filter((q) => q.type === "mcq");
        if (mcqs.length === 0) {
          setToast({ type: "error", message: "No MCQ questions in this set to simulate" });
          return;
        }
        setQuestions(mcqs);
        setGridItems([]);
        setCurrentIdx(0);
        setAnswers([]);
        setGridAnswers([]);
        setConsecutiveWrong(0);
        setStopped(false);
        setFinished(false);
        setStarted(true);
        setSelectedOption(null);
      }
    } catch {
      setToast({ type: "error", message: "Failed to load questions" });
    }
  }, [selectedSetId, sets]);

  /* ── Answer current question ── */
  const submitAnswer = () => {
    if (selectedOption === null) return;
    const q = questions[currentIdx];
    const isCorrect = q.options[selectedOption]?.is_correct || false;
    const newConsecutive = isCorrect ? 0 : consecutiveWrong + 1;

    const newAnswer = {
      questionId: q.id,
      questionText: q.text,
      selectedText: q.options[selectedOption]?.text,
      isCorrect,
      consecutiveWrongAfter: newConsecutive,
    };
    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);
    setConsecutiveWrong(newConsecutive);
    setSelectedOption(null);

    // Check stop rule
    if (stopThreshold && newConsecutive >= stopThreshold) {
      setStopped(true);
      return;
    }

    // Move to next or finish
    if (currentIdx + 1 >= questions.length) {
      setFinished(true);
    } else {
      setCurrentIdx(currentIdx + 1);
    }
  };

  /* ── Grid answer handler ── */
  const submitGridAnswer = (correct) => {
    const item = gridItems[currentIdx];
    const newAnswer = { index: currentIdx, text: item.text, correct };
    const newGridAnswers = [...gridAnswers, newAnswer];
    setGridAnswers(newGridAnswers);

    if (!correct) {
      // Grid stops when student gets one wrong
      setStopped(true);
      return;
    }

    if (currentIdx + 1 >= gridItems.length) {
      setFinished(true);
    } else {
      setCurrentIdx(currentIdx + 1);
    }
  };

  /* ── Reset ── */
  const reset = () => {
    setStarted(false);
    setQuestions([]);
    setGridItems([]);
    setAnswers([]);
    setGridAnswers([]);
    setConsecutiveWrong(0);
    setStopped(false);
    setFinished(false);
    setCurrentIdx(0);
    setSelectedOption(null);
    setSetType("mcq");
  };

  const currentQ = questions[currentIdx];
  const totalCorrect = answers.filter((a) => a.isCorrect).length;
  const totalWrong = answers.filter((a) => !a.isCorrect).length;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Title */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#24292f]">Assessment Simulator</h1>
        <p className="text-sm text-[#57606a] mt-1">
          Simulate answering MCQ questions or Grid items to verify stop-rule logic.
          Pick a competency → set → start the simulation.
        </p>
      </div>

      {/* Step 1: Selection */}
      {!started && (
        <div className="bg-white rounded-xl border border-[#e5e7eb] p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Competency</label>
              <select
                className={selectClass}
                value={selectedComp}
                onChange={(e) => {
                  setSelectedComp(e.target.value);
                  setSelectedSetId("");
                }}
              >
                <option value="">— Select Competency —</option>
                {competencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.title || c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Set</label>
              <select
                className={selectClass}
                value={selectedSetId}
                onChange={(e) => setSelectedSetId(e.target.value)}
                disabled={!selectedComp}
              >
                <option value="">— Select Set —</option>
                {sets.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title} ({s.question_count} Q)
                    {s.stop_rule ? ` [${STOP_RULE_LABELS[s.stop_rule] || s.stop_rule}]` : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedSet && (
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><strong>Title:</strong> {selectedSet.title}</p>
              <p><strong>Description:</strong> {selectedSet.description || "—"}</p>
              <p><strong>Questions:</strong> {selectedSet.question_count}</p>
              <p>
                <strong>Stop Rule:</strong>{" "}
                {selectedSet.stop_rule ? (
                  <span className="text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded">
                    {STOP_RULE_LABELS[selectedSet.stop_rule] || selectedSet.stop_rule}
                  </span>
                ) : (
                  <span className="text-gray-500">None</span>
                )}
              </p>
            </div>
          )}

          <button
            className={btnPrimary}
            disabled={!selectedSetId}
            onClick={startSimulation}
          >
            <HiOutlinePlay className="text-base" />
            Start Simulation
          </button>
        </div>
      )}

      {/* Step 2a: Active MCQ Simulation */}
      {started && !stopped && !finished && setType === "mcq" && currentQ && (
        <div className="space-y-4">
          {/* Status Bar */}
          <div className="flex items-center gap-4 bg-white rounded-xl border border-[#e5e7eb] px-5 py-3">
            <span className="text-sm font-medium text-[#24292f]">
              Question {currentIdx + 1} / {questions.length}
            </span>
            <span className="text-xs text-gray-400">|</span>
            <span className="text-xs font-medium text-green-600 flex items-center gap-1">
              <HiOutlineCheckCircle /> {totalCorrect} correct
            </span>
            <span className="text-xs font-medium text-red-600 flex items-center gap-1">
              <HiOutlineXCircle /> {totalWrong} wrong
            </span>
            <span className="text-xs text-gray-400">|</span>
            <span className={`text-xs font-bold ${
              consecutiveWrong >= (stopThreshold || 999) - 1 && consecutiveWrong > 0
                ? "text-red-600 animate-pulse"
                : consecutiveWrong > 0
                ? "text-orange-500"
                : "text-gray-400"
            }`}>
              Consecutive wrong: {consecutiveWrong}
              {stopThreshold && ` / ${stopThreshold}`}
            </span>
            <div className="ml-auto">
              <button
                onClick={reset}
                className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1"
              >
                <HiOutlineRefresh /> Reset
              </button>
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-6">
            <p className="text-base font-semibold text-[#24292f] mb-4">{currentQ.text}</p>
            {currentQ.description && (
              <p className="text-xs text-gray-500 mb-3">{currentQ.description}</p>
            )}
            <div className="space-y-2">
              {currentQ.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedOption(idx)}
                  className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all ${
                    selectedOption === idx
                      ? "border-[#0078d4] bg-[#eff6ff] text-[#0078d4] font-medium ring-2 ring-[#0078d4]/20"
                      : "border-[#d0d7de] bg-white hover:bg-gray-50 text-[#24292f]"
                  }`}
                >
                  <span className="font-mono text-xs mr-2 text-gray-400">
                    {String.fromCharCode(65 + idx)}.
                  </span>
                  {opt.text}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                className={`${btnPrimary} ${selectedOption === null ? "opacity-50 cursor-not-allowed" : ""}`}
                disabled={selectedOption === null}
                onClick={submitAnswer}
              >
                Submit Answer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2b: Active Grid Simulation */}
      {started && !stopped && !finished && setType === "grid" && gridItems[currentIdx] && (
        <div className="space-y-4">
          {/* Status Bar */}
          <div className="flex items-center gap-4 bg-white rounded-xl border border-[#e5e7eb] px-5 py-3">
            <span className="text-sm font-medium text-[#24292f]">
              Grid Item {currentIdx + 1} / {gridItems.length}
            </span>
            <span className="text-xs text-gray-400">|</span>
            <span className="text-xs font-medium text-green-600 flex items-center gap-1">
              <HiOutlineCheckCircle /> {gridAnswers.filter(a => a.correct).length} correct
            </span>
            {selectedSet?.passing_score && (
              <>
                <span className="text-xs text-gray-400">|</span>
                <span className="text-xs text-purple-600 font-medium">
                  Passing: item #{selectedSet.passing_score}
                </span>
              </>
            )}
            <div className="ml-auto">
              <button onClick={reset} className="text-xs text-gray-500 hover:text-red-500 flex items-center gap-1">
                <HiOutlineRefresh /> Reset
              </button>
            </div>
          </div>

          {/* Grid Item Card */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-6">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Grid Item #{currentIdx + 1}</p>
            <p className="text-lg font-semibold text-[#24292f] mb-6">{gridItems[currentIdx].text}</p>
            <p className="text-sm text-[#57606a] mb-4">Did the student answer this grid item correctly?</p>
            <div className="flex gap-3">
              <button
                onClick={() => submitGridAnswer(true)}
                className="flex-1 px-4 py-3 bg-green-50 border-2 border-green-300 rounded-lg text-green-700 font-semibold hover:bg-green-100 transition-all flex items-center justify-center gap-2"
              >
                <HiOutlineCheckCircle className="text-lg" /> Correct
              </button>
              <button
                onClick={() => submitGridAnswer(false)}
                className="flex-1 px-4 py-3 bg-red-50 border-2 border-red-300 rounded-lg text-red-700 font-semibold hover:bg-red-100 transition-all flex items-center justify-center gap-2"
              >
                <HiOutlineXCircle className="text-lg" /> Wrong
              </button>
            </div>
          </div>

          {/* Progress */}
          {gridAnswers.length > 0 && (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-4">
              <h4 className="text-xs font-semibold text-[#57606a] mb-2 uppercase tracking-wider">Progress</h4>
              <div className="flex flex-wrap gap-1.5">
                {gridAnswers.map((a, i) => (
                  <span key={i} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${a.correct ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    #{a.index + 1} {a.correct ? "✓" : "✗"}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Stopped / Finished */}
      {(stopped || finished) && (
        <div className="space-y-4">
          {/* Result Banner */}
          <div className={`rounded-xl border-2 p-6 text-center ${
            stopped
              ? "border-red-300 bg-red-50"
              : "border-green-300 bg-green-50"
          }`}>
            {stopped ? (
              <>
                <HiOutlineStop className="text-4xl text-red-500 mx-auto mb-2" />
                <h2 className="text-lg font-bold text-red-700">
                  {setType === "grid"
                    ? `STOPPED — Wrong answer at Grid Item #${gridAnswers.length}`
                    : `STOPPED — ${stopThreshold} Consecutive Wrong Answers`}
                </h2>
                <p className="text-sm text-red-600 mt-1">
                  {setType === "grid"
                    ? `Student reached grid item ${gridAnswers.length} of ${gridItems.length}. Score: ${gridAnswers.filter(a => a.correct).length}.`
                    : <>The stop rule <strong>&quot;{selectedSet?.stop_rule}&quot;</strong> triggered at question {answers.length} of {questions.length}.</>}
                </p>
              </>
            ) : (
              <>
                <HiOutlineCheckCircle className="text-4xl text-green-500 mx-auto mb-2" />
                <h2 className="text-lg font-bold text-green-700">
                  Assessment Complete!
                </h2>
                <p className="text-sm text-green-600 mt-1">
                  {setType === "grid"
                    ? `All ${gridItems.length} grid items answered correctly!`
                    : `All ${questions.length} questions answered.`}
                </p>
              </>
            )}
          </div>

          {/* Score Summary */}
          <div className="bg-white rounded-xl border border-[#e5e7eb] p-5">
            <h3 className="text-sm font-semibold text-[#24292f] mb-3">Score Summary</h3>
            {setType === "grid" ? (
              <>
                <div className="grid grid-cols-3 gap-4 text-center mb-4">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-blue-700">{gridAnswers.length}</p>
                    <p className="text-[10px] text-blue-500 font-medium">Attempted</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-green-700">{gridAnswers.filter(a => a.correct).length}</p>
                    <p className="text-[10px] text-green-500 font-medium">Correct</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3">
                    <p className="text-2xl font-bold text-purple-700">{gridAnswers.filter(a => a.correct).length}</p>
                    <p className="text-[10px] text-purple-500 font-medium">Grid Score</p>
                  </div>
                </div>
                {selectedSet?.passing_score && (
                  <p className={`text-sm text-center font-medium ${gridAnswers.filter(a => a.correct).length >= selectedSet.passing_score ? "text-green-600" : "text-red-600"}`}>
                    {gridAnswers.filter(a => a.correct).length >= selectedSet.passing_score ? "✓ PASSED" : "✗ FAILED"} (Passing: {selectedSet.passing_score})
                  </p>
                )}
                <h4 className="text-xs font-semibold text-[#57606a] mb-2 mt-4 uppercase tracking-wider">Grid Item Log</h4>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {gridAnswers.map((a, i) => (
                    <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs ${a.correct ? "bg-green-50" : "bg-red-50"}`}>
                      <span className="font-mono text-gray-400 w-5">#{a.index + 1}</span>
                      {a.correct ? <HiOutlineCheckCircle className="text-green-500" /> : <HiOutlineXCircle className="text-red-500" />}
                      <span className="text-[#24292f] flex-1 truncate">{a.text}</span>
                      <span className={`font-medium ${a.correct ? "text-green-700" : "text-red-700"}`}>{a.correct ? "Correct" : "Wrong"}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
            <>
            <div className="grid grid-cols-3 gap-4 text-center mb-4">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-700">{answers.length}</p>
                <p className="text-[10px] text-blue-500 font-medium">Attempted</p>
              </div>
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-green-700">{totalCorrect}</p>
                <p className="text-[10px] text-green-500 font-medium">Correct</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-red-700">{totalWrong}</p>
                <p className="text-[10px] text-red-500 font-medium">Wrong</p>
              </div>
            </div>

            {/* Answer Log */}
            <h4 className="text-xs font-semibold text-[#57606a] mb-2 uppercase tracking-wider">Answer Log</h4>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {answers.map((a, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-xs ${
                    a.isCorrect ? "bg-green-50" : "bg-red-50"
                  }`}
                >
                  <span className="font-mono text-gray-400 w-5">Q{i + 1}</span>
                  {a.isCorrect ? (
                    <HiOutlineCheckCircle className="text-green-500 flex-shrink-0" />
                  ) : (
                    <HiOutlineXCircle className="text-red-500 flex-shrink-0" />
                  )}
                  <span className="text-[#24292f] flex-1 truncate">{a.questionText}</span>
                  <span className={`font-medium ${a.isCorrect ? "text-green-700" : "text-red-700"}`}>
                    {a.selectedText}
                  </span>
                  <span className="text-gray-400 text-[10px]">
                    streak: {a.consecutiveWrongAfter}
                  </span>
                </div>
              ))}
            </div>
            </>
            )}
          </div>

          <button className={btnPrimary} onClick={reset}>
            <HiOutlineRefresh className="text-base" />
            Start New Simulation
          </button>
        </div>
      )}
    </div>
  );
}
